import React, { useContext, useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Breadcrumbs,
  Link,
  FormControl,
  Select,
  MenuItem,
  Dialog,
  DialogContent,
  DialogContentText,
  LinearProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CloseIcon from "@mui/icons-material/Close";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import {
  getSuppressions,
  clearSuppressionState,
  bulkUpsertSuppressions,
  clearBulkSuppressionMessages,
} from "../../../../../store/suppression/suppressionSlice";
import store from "../../../../../store";
import { formatScheduleName, getSuppressionAccountLabels } from "../../utils/scheduleNameFormatter";
import {
  CloudProviderContext,
  shouldShowAzurePlaceholderView,
} from "../../../../../CloudProviderContext";
import {
  ACCOUNT_DETAIL_FONT_SX,
  ACCOUNT_DETAIL_FONT_FAMILY,
} from "../../../../../components/AccountDetailPolicy/accountDetailTypography";
import { TruncatedTooltipText } from "../../../../../components/AccountDetailPolicy/editAccountTooltipStyles";

const CELL_TEXT = {
  fontSize: "0.8125rem",
  fontWeight: 500,
  color: "#334155",
  lineHeight: 1.4,
};

const HEADER_CELL_SX = {
  ...CELL_TEXT,
  fontWeight: 600,
  fontSize: "0.6875rem",
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: "#64748b",
  py: 1.5,
  px: 1.75,
  borderBottom: "1px solid #e2e8f0",
  backgroundColor: "#fff",
  whiteSpace: "nowrap",
};

const BODY_CELL_SX = {
  ...CELL_TEXT,
  py: 1.5,
  px: 1.75,
  borderBottom: "1px solid #f1f5f9",
  whiteSpace: "nowrap",
  overflow: "hidden",
  verticalAlign: "middle",
};

const SEARCH_FIELD_SX = {
  width: { xs: "100%", sm: 280 },
  minWidth: { xs: 0, sm: 200 },
  "& .MuiOutlinedInput-root": {
    height: 40,
    fontSize: "0.875rem",
    backgroundColor: "white",
    borderRadius: "8px",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": {
      borderColor: "#111827",
      borderWidth: "1px",
    },
  },
  "& .MuiInputBase-input": {
    py: 0,
    height: "100%",
    boxSizing: "border-box",
  },
};

const MENU_ITEM_SX = {
  fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
  fontSize: "0.875rem",
  fontWeight: 500,
  color: "#111827",
  mx: 0.5,
  my: 0.25,
  borderRadius: "6px",
  "&:hover": {
    backgroundColor: "#fff4eb",
  },
  "&.Mui-selected": {
    fontWeight: 600,
    color: "#fff",
    backgroundColor: "#F98125",
    "&:hover": {
      backgroundColor: "#e8741c",
      color: "#fff",
    },
    "&.Mui-focusVisible": {
      backgroundColor: "#e8741c",
      color: "#fff",
    },
  },
};

const SELECT_SX = {
  height: 40,
  fontSize: "0.875rem",
  backgroundColor: "#fff",
  borderRadius: "8px",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#e2e8f0",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#cbd5e1",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#111827",
    borderWidth: "1px",
  },
  "& .MuiSelect-select": {
    py: 0,
    display: "flex",
    alignItems: "center",
  },
  "& .MuiSelect-icon": {
    color: "#94a3b8",
  },
};

const SORT_ICON_ACTIVE_SX = { fontSize: 14, color: "#94a3b8" };
const SORT_ICON_IDLE_SX = { fontSize: 14, color: "#cbd5e1" };

const Suppressions = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { scheduleId } = useParams();
  const { selectedProvider } = useContext(CloudProviderContext);
  const accountLabels = getSuppressionAccountLabels(
    shouldShowAzurePlaceholderView(selectedProvider)
  );

  const { 
    suppressions, 
    isFetchingSuppressions, 
    fetchSuppressionsError,
    isSavingSuppressions,
    saveSuppressionsError,
    saveSuppressionsSuccess,
  } = useSelector((state) => state.suppression);

  // Get selected schedule from sessionStorage
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [scopeFilter, setScopeFilter] = useState("");
  
  // Sorting state
  const [sortField, setSortField] = useState(null); // null, 'framework_name', 'control_code', 'control_title', 'scope', 'check_title', 'created_by', 'created_at'
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
  
  // Reset dialog state
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetConfirmDialogOpen, setResetConfirmDialogOpen] = useState(false);
  const [resetStatus, setResetStatus] = useState({
    step: 0, // 0: not started, 1: resetting, 2: refreshing, 3: done
    message: "",
    error: null,
  });

  // Pagination state
  const [page, setPage] = useState(1);
  const API_PAGE_SIZE = 100; // API fetches 100 records at a time
  const DEFAULT_PAGE_SIZE = 25;
  
  // Get page size from localStorage or use default
  const getStoredPageSize = () => {
    const stored = localStorage.getItem("suppression_suppressions_page_size");
    return stored ? parseInt(stored, 10) : DEFAULT_PAGE_SIZE;
  };
  
  const [pageSize, setPageSize] = useState(getStoredPageSize());
  
  // Store all suppressions data and track fetched API pages
  const [allSuppressions, setAllSuppressions] = useState(null);
  const [fetchedApiPages, setFetchedApiPages] = useState(new Set([1]));

  useEffect(() => {
    // Retrieve schedule data from sessionStorage
    const scheduleData = sessionStorage.getItem("selectedSchedule");
    if (scheduleData) {
      try {
        setSelectedSchedule(JSON.parse(scheduleData));
      } catch (error) {
        console.error("Error parsing selectedSchedule:", error);
      }
    }
  }, []);

  // Fetch suppressions on mount
  useEffect(() => {
    if (scheduleId) {
      dispatch(getSuppressions({ 
        schedule_id: scheduleId, 
        params: { 
          page: 1, 
          page_size: API_PAGE_SIZE 
        } 
      }));
    }
  }, [dispatch, scheduleId]);
  
  // Check if we need to fetch more data when page changes
  useEffect(() => {
    if (!allSuppressions?.items || !scheduleId) return;
    
    // Calculate which API page we need based on current UI page and page size
    const recordsNeeded = page * pageSize;
    const apiPageNeeded = Math.ceil(recordsNeeded / API_PAGE_SIZE);
    
    // If we haven't fetched this API page yet, fetch it
    if (apiPageNeeded > 0 && !fetchedApiPages.has(apiPageNeeded)) {
      dispatch(getSuppressions({ 
        schedule_id: scheduleId, 
        params: { 
          page: apiPageNeeded, 
          page_size: API_PAGE_SIZE 
        } 
      }));
      setFetchedApiPages((prev) => new Set([...prev, apiPageNeeded]));
    }
  }, [page, pageSize, allSuppressions, fetchedApiPages, dispatch, scheduleId]);
  
  // Update allSuppressions when suppressions data is received (merge with existing data)
  useEffect(() => {
    if (suppressions && !isFetchingSuppressions && suppressions.items) {
      setAllSuppressions((prev) => {
        if (!prev) {
          return suppressions;
        }
        // Merge items, avoiding duplicates
        const existingIds = new Set(prev.items.map((s) => s.suppression_id));
        const newItems = suppressions.items.filter(
          (s) => !existingIds.has(s.suppression_id)
        );
        return {
          ...suppressions,
          items: [...prev.items, ...newItems],
          total: Math.max(prev.total || 0, suppressions.total || 0),
        };
      });
    }
  }, [suppressions, isFetchingSuppressions]);

  // Clear state on unmount
  useEffect(() => {
    return () => {
      dispatch(clearSuppressionState());
    };
  }, [dispatch]);

  // Handle back navigation
  const handleBack = () => {
    history.push("/configuration/suppression");
  };

  // Handle breadcrumb navigation
  const handleBreadcrumbClick = (path) => {
    history.push(path);
  };

  // Filter and paginate suppressions client-side
  const filteredSuppressions = useMemo(() => {
    if (!allSuppressions?.items) return { items: [], total: 0, page: 1, page_size: pageSize };
    
    let filtered = [...allSuppressions.items];

    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((suppression) => {
        return (
          suppression.framework_name?.toLowerCase().includes(searchLower) ||
          suppression.control_code?.toLowerCase().includes(searchLower) ||
          suppression.control_title?.toLowerCase().includes(searchLower) ||
          suppression.check_title?.toLowerCase().includes(searchLower) ||
          suppression.created_by?.toLowerCase().includes(searchLower) ||
          suppression.scope?.toLowerCase().includes(searchLower)
        );
      });
    }

    // Filter by scope
    if (scopeFilter) {
      filtered = filtered.filter((suppression) => {
        return suppression.scope?.toUpperCase() === scopeFilter.toUpperCase();
      });
    }

    // Sort data
    if (sortField) {
      filtered.sort((a, b) => {
        let aValue = a[sortField];
        let bValue = b[sortField];

        // Handle null/undefined values
        if (aValue == null) aValue = '';
        if (bValue == null) bValue = '';

        // Convert to string for comparison
        aValue = String(aValue).toLowerCase();
        bValue = String(bValue).toLowerCase();

        // Handle date sorting for created_at
        if (sortField === 'created_at') {
          aValue = a.created_at ? new Date(a.created_at).getTime() : 0;
          bValue = b.created_at ? new Date(b.created_at).getTime() : 0;
        }

        let comparison = 0;
        if (aValue < bValue) {
          comparison = -1;
        } else if (aValue > bValue) {
          comparison = 1;
        }

        return sortDirection === 'asc' ? comparison : -comparison;
      });
    }

    // Calculate pagination
    const total = filtered.length;
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedItems = filtered.slice(startIndex, endIndex);

    return {
      items: paginatedItems,
      total,
      page,
      page_size: pageSize,
    };
  }, [allSuppressions, searchTerm, scopeFilter, sortField, sortDirection, page, pageSize]);
  
  // Handle page size change
  const handlePageSizeChange = (event) => {
    const newPageSize = parseInt(event.target.value, 10);
    setPageSize(newPageSize);
    localStorage.setItem("suppression_suppressions_page_size", newPageSize.toString());
    setPage(1); // Reset to first page when page size changes
  };
  
  // Handle pagination
  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with ascending direction
      setSortField(field);
      setSortDirection('asc');
    }
    setPage(1); // Reset to first page when sorting changes
  };

  // Handle reset all suppressions for this schedule
  const handleResetSuppressions = async () => {
    if (!scheduleId) {
      console.error("Cannot reset: Missing schedule");
      return;
    }

    // Close confirmation dialog and open progress dialog
    setResetConfirmDialogOpen(false);
    setResetDialogOpen(true);
    setResetStatus({
      step: 0,
      message: "",
      error: null,
    });

    try {
      // Helper function to wait for API call to complete
      const waitForSaveOperation = () => {
        return new Promise((resolve, reject) => {
          const checkInterval = setInterval(() => {
            const state = store.getState();
            const suppressionState = state.suppression;
            const currentState = {
              isSaving: suppressionState.isSavingSuppressions,
              success: suppressionState.saveSuppressionsSuccess,
              error: suppressionState.saveSuppressionsError,
            };

            if (!currentState.isSaving) {
              clearInterval(checkInterval);
              if (currentState.error) {
                reject(new Error(currentState.error));
              } else if (currentState.success) {
                resolve(currentState.success);
              } else {
                setTimeout(() => {
                  const finalState = store.getState().suppression;
                  if (finalState.saveSuppressionsError) {
                    reject(new Error(finalState.saveSuppressionsError));
                  } else if (finalState.saveSuppressionsSuccess) {
                    resolve(finalState.saveSuppressionsSuccess);
                  } else {
                    reject(new Error("Operation completed but no result"));
                  }
                }, 200);
              }
            }
          }, 100);

          setTimeout(() => {
            clearInterval(checkInterval);
            reject(new Error("Operation timed out"));
          }, 30000);
        });
      };

      // Step 1: Reset all suppressions
      setResetStatus((prev) => ({
        ...prev,
        step: 1,
        message: "Resetting all suppressions...",
      }));

      dispatch(clearBulkSuppressionMessages());

      // Call API with reset=true, empty payload, no framework_id
      dispatch(bulkUpsertSuppressions({
        schedule_id: scheduleId,
        payload: [],
        queryParams: { 
          reset: true,
        },
      }));

      await waitForSaveOperation();
      console.log("Reset completed successfully");

      setResetStatus((prev) => ({
        ...prev,
        message: "Successfully reset all suppressions",
      }));

      // Step 2: Refresh suppressions data
      setResetStatus((prev) => ({
        ...prev,
        step: 2,
        message: "Refreshing suppressions...",
      }));

      // Reset fetched pages to force fresh data
      setFetchedApiPages(new Set([1]));
      setAllSuppressions(null);

      dispatch(getSuppressions({ 
        schedule_id: scheduleId, 
        params: { 
          page: 1, 
          page_size: API_PAGE_SIZE 
        } 
      }));

      // Wait for suppressions data to be fetched
      await new Promise((resolve, reject) => {
        const checkInterval = setInterval(() => {
          const state = store.getState();
          const suppressionState = state.suppression;
          
          if (!suppressionState.isFetchingSuppressions) {
            clearInterval(checkInterval);
            if (suppressionState.fetchSuppressionsError) {
              reject(new Error(suppressionState.fetchSuppressionsError));
            } else if (suppressionState.suppressions && suppressionState.suppressions.items) {
              setTimeout(() => {
                resolve(suppressionState.suppressions);
              }, 300);
            } else {
              setTimeout(() => {
                const finalState = store.getState().suppression;
                if (finalState.suppressions && finalState.suppressions.items) {
                  setTimeout(() => resolve(finalState.suppressions), 300);
                } else {
                  reject(new Error("Suppressions data not received"));
                }
              }, 500);
            }
          }
        }, 100);

        setTimeout(() => {
          clearInterval(checkInterval);
          reject(new Error("Refresh timed out"));
        }, 30000);
      });

      // Step 3: Done
      setResetStatus((prev) => ({
        ...prev,
        step: 3,
        message: "All operations completed successfully!",
      }));

      // Close dialog after a short delay
      setTimeout(() => {
        setResetDialogOpen(false);
        setResetStatus({
          step: 0,
          message: "",
          error: null,
        });
      }, 1500);
    } catch (error) {
      console.error("Error resetting suppressions:", error);
      setResetStatus((prev) => ({
        ...prev,
        error: getErrorMessage(error),
      }));
    }
  };

  // Helper function to safely extract error message
  const getErrorMessage = (error) => {
    if (!error) return "An unexpected error occurred";
    if (typeof error === "string") return error;
    if (typeof error === "object") {
      if (error.error && typeof error.error === "string") return error.error;
      if (error.message && typeof error.message === "string")
        return error.message;
      if (error.error && typeof error.error === "object") {
        if (error.error.message) return error.error.message;
        if (error.error.error) return error.error.error;
      }
      try {
        const stringified = JSON.stringify(error);
        if (stringified.length > 200) {
          return "An error occurred. Please check the console for details.";
        }
        return stringified;
      } catch {
        return "An unexpected error occurred";
      }
    }
    return String(error);
  };

  // Get pagination info
  const totalPages = filteredSuppressions?.total
    ? Math.ceil(filteredSuppressions.total / pageSize)
    : 0;
  const currentPageStart = filteredSuppressions?.items?.length
    ? (page - 1) * pageSize + 1
    : 0;
  const currentPageEnd = filteredSuppressions?.items?.length
    ? Math.min(page * pageSize, filteredSuppressions.total || 0)
    : 0;

  const renderSortIcon = (field) => {
    if (sortField === field) {
      return sortDirection === "asc" ? (
        <ArrowUpwardIcon sx={SORT_ICON_ACTIVE_SX} />
      ) : (
        <ArrowDownwardIcon sx={SORT_ICON_ACTIVE_SX} />
      );
    }
    return <UnfoldMoreIcon sx={SORT_ICON_IDLE_SX} />;
  };

  const sortableHeaderSx = {
    ...HEADER_CELL_SX,
    cursor: "pointer",
    userSelect: "none",
    "&:hover": {
      backgroundColor: "#f8fafc",
    },
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        border: "1px solid #e5e7eb",
        borderRadius: "12px",
        p: { xs: 1.5, sm: 2.5 },
        backgroundColor: "#f3f4f6",
        boxSizing: "border-box",
        ...ACCOUNT_DETAIL_FONT_SX,
      }}
    >
      {/* Breadcrumbs */}
      <Box sx={{ mb: 2 }}>
        <Breadcrumbs
          separator={
            <Box
              component="span"
              sx={{
                color: "#94a3b8",
                fontSize: "0.8125rem",
                mx: 0.5,
                display: "flex",
                alignItems: "center",
              }}
            >
              /
            </Box>
          }
          aria-label="breadcrumb"
          sx={{
            "& .MuiBreadcrumbs-ol": {
              flexWrap: "nowrap",
            },
          }}
        >
          <Link
            component="button"
            variant="body2"
            onClick={() => handleBreadcrumbClick("/configuration/suppression")}
            sx={{
              color: "#64748b",
              textDecoration: "none",
              fontSize: "0.8125rem",
              fontWeight: 400,
              transition: "color 0.2s ease",
              "&:hover": {
                color: "#334155",
              },
              cursor: "pointer",
              px: 0.5,
            }}
          >
            Schedules
          </Link>
          <Typography
            variant="body2"
            sx={{
              color: "#475569",
              fontSize: "0.8125rem",
              fontWeight: 500,
              px: 0.5,
            }}
          >
            Suppressions
          </Typography>
        </Breadcrumbs>
      </Box>

      {/* Header with Back Button on Right */}
      <Box
        sx={{
          mb: 2,
          display: "flex",
          alignItems: { xs: "stretch", sm: "flex-start" },
          justifyContent: "space-between",
          flexDirection: { xs: "column", sm: "row" },
          gap: 1.5,
          borderBottom: "1px solid #e5e7eb",
          pb: 1.5,
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              fontWeight: 700,
              color: "#111827",
              fontSize: "1rem",
              mb: 0.5,
            }}
          >
            Suppressions
          </Typography>
          <Typography
            sx={{
              color: "#64748b",
              fontSize: "0.875rem",
              fontWeight: 400,
              lineHeight: 1.5,
            }}
          >
            {selectedSchedule
              ? `Schedule: ${formatScheduleName(selectedSchedule)} | ${accountLabels.noun}: ${selectedSchedule.account_number || selectedSchedule.account_id || "N/A"}`
              : "Viewing suppressions for selected schedule"}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          onClick={handleBack}
          startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
          sx={{
            color: "#334155",
            backgroundColor: "#fff",
            border: "1px solid #e2e8f0",
            borderRadius: "8px",
            textTransform: "none",
            fontSize: "0.875rem",
            fontWeight: 500,
            height: 40,
            px: 2,
            flexShrink: 0,
            alignSelf: { xs: "stretch", sm: "flex-start" },
            "&:hover": {
              backgroundColor: "#f8fafc",
              borderColor: "#cbd5e1",
              color: "#111827",
            },
          }}
        >
          Back
        </Button>
      </Box>

      {/* Loading State */}
      {isFetchingSuppressions && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 8,
            border: "1px solid #e2e8f0",
            borderRadius: "12px",
            backgroundColor: "#fff",
          }}
        >
          <CircularProgress size={32} sx={{ color: "#64748b" }} />
          <Typography
            sx={{
              ml: 2,
              color: "#64748b",
              fontSize: "0.875rem",
              fontWeight: 500,
            }}
          >
            Loading suppressions...
          </Typography>
        </Box>
      )}

      {/* Error State */}
      {fetchSuppressionsError && !isFetchingSuppressions && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            backgroundColor: "#fef2f2",
            borderRadius: "8px",
            border: "1px solid #fecaca",
          }}
        >
          <Typography
            sx={{
              color: "#991b1b",
              fontSize: "0.875rem",
              fontWeight: 500,
            }}
          >
            {getErrorMessage(fetchSuppressionsError)}
          </Typography>
        </Box>
      )}

      {/* Suppressions Table */}
      {allSuppressions && !isFetchingSuppressions && !fetchSuppressionsError && (
        <Box
          sx={{
            border: "1px solid #e2e8f0",
            borderRadius: "12px",
            backgroundColor: "#fff",
            overflow: "hidden",
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              px: { xs: 1.5, sm: 2 },
              py: 1.5,
              borderBottom: "1px solid #e2e8f0",
              backgroundColor: "#fff",
              display: "flex",
              justifyContent: "space-between",
              alignItems: { xs: "stretch", sm: "center" },
              flexDirection: { xs: "column", sm: "row" },
              gap: 1.5,
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                color: "#111827",
                fontSize: "0.875rem",
              }}
            >
              Suppressions
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                alignItems: "center",
                flexWrap: "wrap",
                width: { xs: "100%", sm: "auto" },
              }}
            >
              <TextField
                size="small"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                    </InputAdornment>
                  ),
                }}
                sx={SEARCH_FIELD_SX}
              />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={scopeFilter}
                  onChange={(e) => {
                    setScopeFilter(e.target.value);
                    setPage(1); // Reset to first page when filter changes
                  }}
                  displayEmpty
                  sx={SELECT_SX}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
                        borderRadius: "8px",
                        boxShadow: "0 4px 12px rgba(15, 23, 42, 0.08)",
                        border: "1px solid #e2e8f0",
                        mt: 0.5,
                        overflow: "hidden",
                        "& .MuiList-root": {
                          py: 0.5,
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="" sx={MENU_ITEM_SX}>
                    All Scopes
                  </MenuItem>
                  <MenuItem value="CONTROL" sx={MENU_ITEM_SX}>
                    Control
                  </MenuItem>
                  <MenuItem value="CHECK" sx={MENU_ITEM_SX}>
                    Check
                  </MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                startIcon={<RestartAltIcon sx={{ fontSize: 18 }} />}
                onClick={() => setResetConfirmDialogOpen(true)}
                disabled={isSavingSuppressions || (filteredSuppressions?.total || 0) === 0}
                sx={{
                  color: "#334155",
                  borderColor: "#e2e8f0",
                  textTransform: "none",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  height: 40,
                  px: 2,
                  borderRadius: "8px",
                  backgroundColor: "#fff",
                  "&:hover": {
                    backgroundColor: "#f8fafc",
                    borderColor: "#cbd5e1",
                    color: "#111827",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#fff",
                    borderColor: "#e5e7eb",
                    color: "#9ca3af",
                  },
                }}
              >
                Reset
              </Button>
            </Box>
          </Box>

          <TableContainer
            sx={{
              width: "100%",
              maxWidth: "100%",
              overflowX: "auto",
              WebkitOverflowScrolling: "touch",
              "&::-webkit-scrollbar": {
                height: "8px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#f1f5f9",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#cbd5e1",
                borderRadius: "4px",
                "&:hover": {
                  backgroundColor: "#94a3b8",
                },
              },
            }}
          >
            <Table
              size="small"
              sx={{
                width: "100%",
                minWidth: 960,
                tableLayout: "fixed",
              }}
            >
              <colgroup>
                <col style={{ width: "14%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "22%" }} />
                <col style={{ width: "8%" }} />
                <col style={{ width: "22%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "12%" }} />
              </colgroup>
              <TableHead>
                <TableRow>
                  <TableCell
                    onClick={() => handleSort("framework_name")}
                    sx={sortableHeaderSx}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      Framework Name
                      {renderSortIcon("framework_name")}
                    </Box>
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort("control_code")}
                    sx={sortableHeaderSx}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      Control Id
                      {renderSortIcon("control_code")}
                    </Box>
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort("control_title")}
                    sx={sortableHeaderSx}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      Control Title
                      {renderSortIcon("control_title")}
                    </Box>
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort("scope")}
                    sx={sortableHeaderSx}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      Scope
                      {renderSortIcon("scope")}
                    </Box>
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort("check_title")}
                    sx={sortableHeaderSx}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      Check Title
                      {renderSortIcon("check_title")}
                    </Box>
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort("created_by")}
                    sx={sortableHeaderSx}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      Updated By
                      {renderSortIcon("created_by")}
                    </Box>
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort("created_at")}
                    sx={sortableHeaderSx}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      Updated On
                      {renderSortIcon("created_at")}
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSuppressions?.items && filteredSuppressions.items.length > 0 ? (
                  filteredSuppressions.items.map((suppression, index) => (
                    <TableRow
                      key={suppression.suppression_id || index}
                      sx={{
                        "&:hover": {
                          backgroundColor: "#f8fafc",
                        },
                      }}
                    >
                      <TableCell sx={BODY_CELL_SX}>
                        <TruncatedTooltipText sx={CELL_TEXT}>
                          {suppression.framework_name || "N/A"}
                        </TruncatedTooltipText>
                      </TableCell>
                      <TableCell sx={BODY_CELL_SX}>
                        <TruncatedTooltipText sx={CELL_TEXT}>
                          {suppression.control_code || "N/A"}
                        </TruncatedTooltipText>
                      </TableCell>
                      <TableCell sx={BODY_CELL_SX}>
                        <TruncatedTooltipText sx={CELL_TEXT}>
                          {suppression.control_title || "N/A"}
                        </TruncatedTooltipText>
                      </TableCell>
                      <TableCell sx={BODY_CELL_SX}>
                        <TruncatedTooltipText
                          sx={{ ...CELL_TEXT, fontWeight: 600 }}
                        >
                          {suppression.scope || "N/A"}
                        </TruncatedTooltipText>
                      </TableCell>
                      <TableCell sx={BODY_CELL_SX}>
                        <TruncatedTooltipText sx={CELL_TEXT}>
                          {suppression.check_title || "N/A"}
                        </TruncatedTooltipText>
                      </TableCell>
                      <TableCell sx={BODY_CELL_SX}>
                        <TruncatedTooltipText sx={CELL_TEXT}>
                          {suppression.created_by || "N/A"}
                        </TruncatedTooltipText>
                      </TableCell>
                      <TableCell sx={BODY_CELL_SX}>
                        <TruncatedTooltipText
                          sx={CELL_TEXT}
                          title={
                            suppression.created_at
                              ? new Date(suppression.created_at).toLocaleString()
                              : "N/A"
                          }
                        >
                          {suppression.created_at
                            ? new Date(suppression.created_at).toLocaleString()
                            : "N/A"}
                        </TruncatedTooltipText>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      align="center"
                      sx={{ ...BODY_CELL_SX, py: 6 }}
                    >
                      <Typography sx={{ ...CELL_TEXT, color: "#64748b" }}>
                        No suppressions found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
              px: 2,
              py: 1,
              borderTop: "1px solid #e2e8f0",
              backgroundColor: "#f8fafc",
              minHeight: 48,
            }}
          >
            <Typography sx={{ color: "#64748b", fontSize: "0.75rem" }}>
              Rows per page
            </Typography>
            <FormControl size="small">
              <Select
                value={pageSize}
                onChange={handlePageSizeChange}
                sx={{
                  fontSize: "0.75rem",
                  height: 28,
                  backgroundColor: "#fff",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e2e8f0",
                  },
                }}
              >
                <MenuItem value={10} sx={{ fontSize: "0.8rem" }}>
                  10
                </MenuItem>
                <MenuItem value={25} sx={{ fontSize: "0.8rem" }}>
                  25
                </MenuItem>
                <MenuItem value={50} sx={{ fontSize: "0.8rem" }}>
                  50
                </MenuItem>
                <MenuItem value={100} sx={{ fontSize: "0.8rem" }}>
                  100
                </MenuItem>
              </Select>
            </FormControl>
            <Typography
              sx={{ color: "#64748b", fontSize: "0.75rem", ml: 0.5 }}
            >
              {filteredSuppressions?.total
                ? `${currentPageStart}–${currentPageEnd} of ${filteredSuppressions.total}`
                : "0 of 0"}
            </Typography>
            <IconButton
              size="small"
              onClick={() => setPage(1)}
              disabled={page === 1}
              sx={{ color: page === 1 ? "#d1d5db" : "#64748b" }}
            >
              <FirstPageIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              sx={{ color: page === 1 ? "#d1d5db" : "#64748b" }}
            >
              <ArrowBackIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page >= totalPages}
              sx={{ color: page >= totalPages ? "#d1d5db" : "#64748b" }}
            >
              <ArrowForwardIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setPage(totalPages)}
              disabled={page >= totalPages}
              sx={{ color: page >= totalPages ? "#d1d5db" : "#64748b" }}
            >
              <LastPageIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>
      )}

      {/* Reset Confirmation Dialog */}
      <Dialog
        open={resetConfirmDialogOpen}
        onClose={() => setResetConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "none",
            border: "1px solid #e2e8f0",
            overflow: "hidden",
            ...ACCOUNT_DETAIL_FONT_SX,
          },
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            backgroundColor: "#F98125",
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "1.15rem",
              color: "#FFFFFF",
              letterSpacing: "-0.02em",
              lineHeight: 1.2,
            }}
          >
            Confirm Reset
          </Typography>
          <button
            type="button"
            aria-label="Close"
            onClick={() => setResetConfirmDialogOpen(false)}
            style={{
              padding: 0,
              margin: 0,
              border: "none",
              background: "transparent",
              color: "#FFFFFF",
              fontSize: 28,
              fontWeight: 700,
              lineHeight: 1,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              opacity: 1,
              boxShadow: "none",
              flexShrink: 0,
            }}
          >
            <span aria-hidden="true">×</span>
          </button>
        </Box>
        <DialogContent sx={{ px: 3, py: 2.5 }}>
          <DialogContentText
            sx={{
              color: "#334155",
              fontSize: "0.9375rem",
              lineHeight: 1.5,
            }}
          >
            Are you sure you want to reset all the suppressions?
          </DialogContentText>
        </DialogContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
            padding: "16px 24px",
            borderTop: "1px solid #e2e8f0",
          }}
        >
          <Button
            onClick={() => setResetConfirmDialogOpen(false)}
            sx={{
              color: "#64748b",
              textTransform: "none",
              fontSize: "0.875rem",
              fontWeight: 500,
              "&:hover": {
                backgroundColor: "#f1f5f9",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleResetSuppressions}
            variant="contained"
            sx={{
              backgroundColor: "#ef4444",
              color: "white",
              textTransform: "none",
              fontSize: "0.875rem",
              fontWeight: 500,
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#dc2626",
                boxShadow: "none",
              },
            }}
          >
            Reset
          </Button>
        </Box>
      </Dialog>

      {/* Reset Status Dialog */}
      <Dialog
        open={resetDialogOpen}
        onClose={() => {
          if (resetStatus.step === 3 || resetStatus.error) {
            setResetDialogOpen(false);
            setResetStatus({
              step: 0,
              message: "",
              error: null,
            });
          }
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "none",
            border: "1px solid #e2e8f0",
            overflow: "hidden",
            ...ACCOUNT_DETAIL_FONT_SX,
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "#F98125",
            padding: "16px 24px",
            gap: 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 700,
                color: "#FFFFFF",
                fontSize: "1.15rem",
                letterSpacing: "-0.02em",
                lineHeight: 1.2,
                mb: 0.5,
              }}
            >
              {resetStatus.error ? "Error Resetting Suppressions" : "Resetting Suppressions"}
            </Typography>
            <Typography
              sx={{
                color: "rgba(255,255,255,0.9)",
                fontSize: "0.875rem",
                fontWeight: 400,
              }}
            >
              {resetStatus.error ? "An error occurred while resetting" : "Processing reset for this schedule"}
            </Typography>
          </Box>
          <IconButton
            onClick={() => {
              if (resetStatus.step === 3 || resetStatus.error) {
                setResetDialogOpen(false);
                setResetStatus({
                  step: 0,
                  message: "",
                  error: null,
                });
              }
            }}
            disabled={resetStatus.step > 0 && resetStatus.step < 3 && !resetStatus.error}
            sx={{
              color: "#FFFFFF",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.12)",
              },
              "&.Mui-disabled": {
                color: "rgba(255,255,255,0.5)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <DialogContent sx={{ padding: "24px" }}>
          {resetStatus.error ? (
            <DialogContentText sx={{ color: "error.main", mb: 2 }}>
              {resetStatus.error}
            </DialogContentText>
          ) : (
            <>
              {resetStatus.step > 0 && resetStatus.message && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: "#1e293b" }}>
                    {resetStatus.step === 1 ? "Resetting Suppressions" : resetStatus.step === 2 ? "Refreshing Data" : "Completed"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#64748b", mb: 1.5 }}>
                    {resetStatus.message}
                  </Typography>
                  {resetStatus.step === 1 && <LinearProgress sx={{ mt: 1, borderRadius: 1 }} />}
                  {resetStatus.step === 2 && <LinearProgress sx={{ mt: 1, borderRadius: 1 }} />}
                </Box>
              )}
              {resetStatus.step === 3 && (
                <Box sx={{ textAlign: "center", py: 2 }}>
                  <Typography variant="body1" sx={{ color: "#10b981", fontWeight: 500 }}>
                    All operations completed successfully!
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Suppressions;
